import type { BoxAnchor, TrackingInterval } from "../contracts";
import type { BoxCoordinates } from "../../utils/coordinates";

export const RECOVERY_LIMITS = { maxRequests: 20, concurrency: 14, timeoutMs: 60_000 } as const;

export interface RecoveryFrameRequest {
  timestamp: number;
  maxSize: number;
  /** Target frames must match an existing decoded PTS; reference anchors may lie inside a frame. */
  exact?: boolean;
}

export interface RecoveryImage {
  timestamp: number;
  duration: number;
  width: number;
  height: number;
  sourceWidth: number;
  sourceHeight: number;
  dataUrl: string;
}

export interface RecoveryInput {
  interval: TrackingInterval;
  trackLabel?: string;
  target: RecoveryImage;
  references: readonly [{ anchor: BoxAnchor; image: RecoveryImage }, { anchor: BoxAnchor; image: RecoveryImage }];
}

export interface RecoveryUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
}

export type RecoveryOutcome = ({ status: "found"; box: BoxCoordinates } | { status: "no_match" }) & {
  requestId?: string;
  usage?: RecoveryUsage;
  timings?: { totalMs: number };
};

/** Stable for one original output run and video, including local tracker reruns. */
export interface FrameRecoveryProvider {
  key: string;
  label: string;
  recover(input: RecoveryInput, signal: AbortSignal): Promise<RecoveryOutcome>;
}

export interface RecoveryOptions {
  enabled: boolean;
  provider?: FrameRecoveryProvider;
}

export type RecoveryStatus =
  | "queued"
  | "extracting"
  | "requesting"
  | "refining"
  | "recovered"
  | "no_match"
  | "failed"
  | "budget_exhausted";

export type RecoveryCallOutcome = "found" | "no_match" | "failed";

/** Actual model calls for the original video run; cache hits and refinement do not add calls. */
export interface RecoveryOutcomeCounts {
  found: number;
  noMatch: number;
  failed: number;
  pending: number;
}

export interface RecoveryIntervalState {
  status: RecoveryStatus;
  /** Model response outcome, independent of subsequent local refinement success or failure. */
  outcome?: RecoveryCallOutcome;
  targetTime?: number;
  recoveredFrames?: number;
  requestId?: string;
  usage?: RecoveryUsage;
  timings?: RecoveryOutcome["timings"];
  error?: string;
}

export interface RecoveryActivity {
  enabled: boolean;
  available: boolean;
  providerLabel?: string;
  requestsUsed: number;
  maxRequests: number;
  concurrency: number;
  activeRequests: number;
  outcomes: RecoveryOutcomeCounts;
  intervals: ReadonlyMap<string, RecoveryIntervalState>;
}

export interface RecoveryLineage {
  anchorTime: number;
  requestId?: string;
  provider: string;
}

export function isRecoveryPending(state: RecoveryIntervalState | undefined): boolean {
  return state !== undefined && ["queued", "extracting", "requesting", "refining"].includes(state.status);
}
